import { SxProps, TextField, Theme, debounce } from "@mui/material";
import { ChangeEvent, ReactNode, useEffect, useMemo, useState } from "react";
import { unwrapResult } from "@reduxjs/toolkit"
import { useLocalization } from "@fluent/react";
import { useAppDispatch } from "../../app/hooks";
import { deviceExists } from "./deviceSlice";


interface DeviceSerialNoFieldProps {
    value: string
    onChange: (v: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
    onValidationResult: (valid: boolean) => void
    sx?: SxProps<Theme>;
    label: ReactNode
    fullWidth?: boolean
    required?: boolean
}
export const DeviceSerialNoField = (props: DeviceSerialNoFieldProps) => {
    const { sx, value, onChange, required, fullWidth, label, onValidationResult } = props;
    const dispatch = useAppDispatch()
    const { l10n } = useLocalization()

    const [status, setStatus] = useState<undefined | 'checking' | 'ok' | 'exists'>(undefined)

    const check = useMemo(
        () =>
            debounce(async (pattern: string) => {
                setStatus("checking");
                try {
                    const { exists } = await dispatch(deviceExists(pattern)).then(unwrapResult);

                    const valid = !exists;
                    setStatus(valid ? "ok" : "exists");
                    onValidationResult?.(valid);
                } catch (err) {
                    setStatus(undefined);
                    onValidationResult?.(false);
                }
            }, 600),
        [dispatch, onValidationResult]
    );
    
    useEffect(() => {
        return () => {
            check.clear?.();
        };
    }, [check]);

    const error: boolean = value === '' || status === 'exists'
    let helperText: ReactNode = <></>
    switch (status) {
        case 'checking':
            helperText = l10n.getString("device-serialno-checking", null, "_checking")
            break
        case 'exists':
            helperText = l10n.getString("device-error-serialno-exists", null, "_exists")

            break
        case 'ok':
            helperText = ' '
            break
        default:
            helperText = l10n.getString("device-enter-serialno-prompt", null, "_prompt")
            break;
    }

    return <TextField
        value={value}
        onChange={e => {
            onChange(e)
            check(e.target.value)
        }}
        error={error}
        helperText={helperText}
        required={required}
        fullWidth={fullWidth}
        label={label}
    />
}
